import router from '@/router';
import useApis from '@/useApis';
import useApisV1 from '@/useApiV1';
import TableDataAddDefaultValue from '@/utils/tableDataAddDefaultValue';
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from 'vuex-module-decorators';
import store from '../index';

export interface AdvancedQueryConditionModal {
    createdAtFrom?: string;
    createdAtTo?: string;
    updatedAtFrom?: string;
    updatedAtTo?: string;
    lastLoginAtFrom?: string;
    lastLoginAtTo?: string;
    name?: string;
    nickname?: string;
    haveUserGroup?: string;
    email?: string;
    mobilePhone?: string;
    phone?: string;
}

export interface TableFilterModel {
    page?: number;
    size?: number;
    searchKey?: string;
    searchType?: string;
    advancedQueryCondition?: AdvancedQueryConditionModal;
    field?: string;
    order?: string;
}

export interface MainState {
    viewsColumns: Map<string, any[]>;
    viewsData: Map<string, Map<string, any>>,
    filterData: Map<string, TableFilterModel>,
    menus: any[];
    viewId: any;
}

@Module({ name: 'MainStore', dynamic: true, namespaced: true, store })
class MainStore extends VuexModule implements MainState {
  viewsColumns: any = {};
  viewsData: any = {};
  filterData: any = {};
  menus: any[] = [];
  viewId: any = null;

  @Mutation
  private setColumns ({ id, columns }) {
    this.viewsColumns[id] = columns;
  }

  @Mutation
  private setViewsData ({ id, dataObj }) {
    this.viewsData[id] = dataObj;
  }

  @Mutation
  private setMenus (menus: any[]) {
    this.menus = menus;
  }

  @Mutation
  private setViewId (viewId: any) {
      this.viewId = viewId;
  }

  @Mutation
  private setFilterData ({ id, dataObj }) {
    this.filterData[id] = dataObj;
  }

  private get GetAdvancedQueryCondition () {
      const filters = this.filterData[this.viewId];
      if (!filters || !filters.advancedQueryCondition) {
          return null;
      }
      const withValueMap: AdvancedQueryConditionModal = {};
      let withValue = false;
      for (const key in filters.advancedQueryCondition) {
          if (filters.advancedQueryCondition[key]) {
            withValueMap[key] = filters.advancedQueryCondition[key];
            withValue = true;
          }
      }
      return withValue ? JSON.stringify(withValueMap) : null;
  }

  public get getViewObj (): any {
    const viewObj = this.menus.find((v) => `${v.id}` === this.viewId);
    return viewObj || {};
  }

  // 获取table 表头数据，以及表头排序逻辑
  @Action
  public getViewColumns (viewId: string) {
    const ApisV1 = useApisV1();
    // TODO 该接口调用太频繁会报错。
    ApisV1({
        url: '/api/v1/custom_field_list/show.json',
        method: 'GET',
        params: {
            view_id: viewId
        }
    }).success(({ result = { customShow: [], defaultShow: [], sortFields: '' } }: any) => {
        const { customShow, defaultShow, sortFields } = result;
        console.log(result);
        console.log(customShow);
        const showColumns = customShow.concat(defaultShow);
        const sortFieldsArray: any[] = JSON.parse(sortFields);
        const viewColumns: any[] = [];
        // 根据后端返回的排序规则， 重新排序表格顺序
        sortFieldsArray.forEach((sortItem: any) => {
            const findColumn = showColumns.find((v) => v.id === sortItem.id);
            if (findColumn) {
                viewColumns.push(findColumn);
            }
        })
        this.setColumns({ id: viewId, columns: viewColumns });
    })
  }

  // 获取客户数据
  @Action
  private getCustomerData (viewData: any) {
    const ApisV1 = useApisV1();
    const filters: TableFilterModel = this.filterData[this.viewId];
    ApisV1({
        url: '/api/v1/users.json',
        method: 'GET',
        params: {
            _asc: (filters.order === 'ascend' && filters.field) ? filters.field : '',
            _count: filters.size,
            _desc: (filters.order === 'descend' && filters.field) ? filters.field : '',
            _fields: viewData.fields,
            _filter: viewData.filter,
            _page: filters.page,
            advancedQueryCondition: this.GetAdvancedQueryCondition,
            searchType: filters.searchType,
            searchKey: filters.searchKey
        }
    }).success(({ result }) => {
        this.setViewsData({
            id: `${viewData.id}`,
            dataObj: {
                count: result._total,
                list: TableDataAddDefaultValue(viewData.fields, result.customers || [])
            }
        })
    })
  }

  // 获取客户组数据
  @Action
  private getUserGroupData (viewData: any) {
    const Apis = useApis();
    const filters = this.filterData[this.viewId];
    Apis.OpenUserGroupApi().listUserGroupByFields({
        page: { 
            pageSize: filters.size,
            pageNumber: filters.page,
            orderBy: 'id DESC'
        },
        fields: viewData.fields,
        key: null
    }).then((result) => {
        this.setViewsData({
            id: `${viewData.id}`,
            dataObj: {
                count: result.recordCount,
                list: TableDataAddDefaultValue(viewData.fields, result.list || [])
            }
        })
    })
  }

  // 共用的同一套，又由于客户、客户组api接口不一致，在此方法里分发
  @Action
  private getViewData () {
      const viewObj = this.menus.find((v) => `${v.id}` === this.viewId);
      if (viewObj) {
          console.log(viewObj);
          if (viewObj.title === 'VIEW_TITLE_USER' || viewObj.title === 'VIEW_TITLE_USER_BLACKLIST') {
            this.getCustomerData(viewObj);
          } else if (viewObj.title === 'VIEW_TITLE_USER_GROUP') {
            this.getUserGroupData(viewObj);
          }
      }
  }

  // 页面第一次加载 初始化数据
  @Action
  public init (nowRouterId: string) {
      const ApisV1 = useApisV1();
      ApisV1({
        url: '/api/v1/views_new.json',
        method: 'GET',
        params: {
            _asc: 'position',
            _count: 9,
            _do_count: false,
            _filter: { 
                $in: {
                    type: ['user', 'userGroup'] 
                }
            },
            _page: 1
        }
      }).success(({ result = { views: [] } }: any) => {
        if (result.views && result.views.length) {
            this.setMenus(result.views);
            let viewId = nowRouterId;
            // 如果页面url 上有视图id 则直接将改id设置为下次打开的默认id， 否则判断vuex 里面是否有上传打开的id。
            if (nowRouterId && result.views.find(v => `${v.id}` === `${nowRouterId}`)) {
                
            } else if (this.viewId) {
                router.replace(`/fe-subapp/index/${this.viewId}`);
                viewId = this.viewId;
            } else {
                router.replace(`/fe-subapp/index/${result.views[0].id}`);
                viewId = `${result.views[0].id}`;
            }
            this.changeView(viewId);
        }
      });
  }

  @Action
  public changeView (viewId: string) {
      if (this.viewId === viewId) {
          return null;
      }
      this.setViewId(viewId);
      let filterData = this.filterData[viewId];
      if (!filterData) {
        filterData = {
            page: 1,
            size: 20
        }
      }
      this.setFilterData({
          id: viewId,
          dataObj: {
              ...filterData
          }
      })
      this.getViewColumns(viewId);
      this.getViewData();
  }

  // 表格数据翻页、搜索、高级搜索
  @Action
  public changeDataFilter (filterData: TableFilterModel) {
    this.setFilterData({
        id: this.viewId,
        dataObj: {
            ...this.filterData[this.viewId],
            ...filterData
        }
    });
    this.getViewData();
  }
}

export const MainModule = getModule(MainStore);
